import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { orderBy } from 'lodash';
import TextEditBlock from '../invoice/texteditblock';
import { Button } from '@material-ui/core';
import EmailSender from '../modules/emailsender';
import Ajax from '../modules/ajax';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { getCargoProviders } from '../modules/cargopricingdata';
import { updateOrderStatus } from '../modules/orderdata';

const ORDER_STATUS_OPTIONS = [
    { value: 1, label: 'Draft' },
    { value: 2, label: 'SendPending' },
    { value: 3, label: 'SendFailed' },
    { value: 4, label: 'Ordered' },
    { value: 5, label: 'InProduction' },
    { value: 6, label: 'Packed' },
    { value: 7, label: 'Loaded' },
    { value: 8, label: 'Delivered' },
    { value: 9, label: 'DeliveredNonInvoicable' },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
    },
}));

const emptyOrderRow = () => ({
    orderNumber: '',
    groupedWithOrderNumber: '',
    boxes: '',
    parcels: '',
    palletsEur: '',
    palletsTeho: '',
});

export default function DebugInfo(props) {
    const wsEvents = useSelector((state) => state.appstate.debugWsEvents);
    const lifecycleEvents = useSelector((state) => state.appstate.debugLifecycleEvents);
    const cargoProviders = useSelector((state) => state.cargopricingdata.providers);
    const dispatch = useDispatch();
    const classes = useStyles();
    const [toAddr, setToAddr] = React.useState('');
    const [fromAddr, setFromAddr] = React.useState('');
    const [fromName, setFromName] = React.useState('');
    const [forceRunDailySummary, setForceRunDailySummary] = React.useState(false);
    const [pollerStatus, setPollerStatus] = React.useState(null);
    const [shipmentProviderId, setShipmentProviderId] = React.useState('');
    const [shipmentOrders, setShipmentOrders] = React.useState([emptyOrderRow()]);
    const [shipmentStatus, setShipmentStatus] = React.useState(null);
    const [statusOrderNumber, setStatusOrderNumber] = React.useState('');
    const [statusNewStatus, setStatusNewStatus] = React.useState('');
    const [statusReason, setStatusReason] = React.useState('');
    const [orderStatusUpdateStatus, setOrderStatusUpdateStatus] = React.useState(null);

    React.useEffect(() => {
        if (cargoProviders == null) {
            dispatch(getCargoProviders());
        }
    }, [cargoProviders, dispatch]);

    const onChange = (evt) => {
        switch (evt.target.name) {
            case 'toAddr':
                setToAddr(evt.target.value);
                break;
            case 'fromAddr':
                setFromAddr(evt.target.value);
                break;
            case 'fromName':
                setFromName(evt.target.value);
                break;
            default:
                break;
        }
    };

    const onSend = async () => {
        await EmailSender.sendTest(toAddr, 'test msg', fromAddr, fromName);
    };

    const onRunPoller = async () => {
        setPollerStatus('running');
        try {
            await Ajax.post('api/debug/runpoller/', { forceRunDailySummary });
            setPollerStatus('ok');
        } catch (err) {
            setPollerStatus('error');
        }
    };

    const onShipmentOrderChange = (index, field, value) => {
        setShipmentOrders((current) => {
            const updated = [...current];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const onAddShipmentOrderRow = () => {
        setShipmentOrders((current) => [...current, emptyOrderRow()]);
    };

    const onRemoveShipmentOrderRow = (index) => {
        setShipmentOrders((current) => current.filter((_, i) => i !== index));
    };

    const toIntOrNull = (value) => (value === '' || value == null ? null : parseInt(value, 10));

    const onInjectShipment = async () => {
        setShipmentStatus('running');
        try {
            await Ajax.post('api/debug/injectshipment/', {
                providerId: shipmentProviderId,
                orders: shipmentOrders.map((o) => ({
                    orderNumber: toIntOrNull(o.orderNumber),
                    groupedWithOrderNumber: toIntOrNull(o.groupedWithOrderNumber),
                    boxes: toIntOrNull(o.boxes),
                    parcels: toIntOrNull(o.parcels),
                    palletsEur: toIntOrNull(o.palletsEur),
                    palletsTeho: toIntOrNull(o.palletsTeho),
                })),
            });
            setShipmentStatus('ok');
        } catch (err) {
            setShipmentStatus('error');
        }
    };

    const onChangeOrderStatus = async () => {
        setOrderStatusUpdateStatus('running');
        const ok = await updateOrderStatus(parseInt(statusNewStatus, 10), parseInt(statusOrderNumber, 10), statusReason)(
            dispatch
        );
        setOrderStatusUpdateStatus(ok ? 'ok' : 'error');
    };

    return (
        <div className={classes.root}>
            <Grid container spacing={3} alignItems={'flex-end'} alignContent={'flex-end'} justifyContent={'flex-end'}>
                <Grid item xs={12}>
                    <TextEditBlock
                        label={'To email'}
                        name={'toAddr'}
                        value={toAddr}
                        onChange={onChange}></TextEditBlock>
                    <TextEditBlock
                        label={'From email'}
                        name={'fromAddr'}
                        value={fromAddr}
                        onChange={onChange}></TextEditBlock>
                    <TextEditBlock
                        label={'From name'}
                        name={'fromName'}
                        value={fromName}
                        onChange={onChange}></TextEditBlock>
                    <Button onClick={onSend}>Send</Button>
                </Grid>
                <Grid item xs={12}>
                    <h4>Order poller</h4>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={forceRunDailySummary}
                                    onChange={(e) => setForceRunDailySummary(e.target.checked)}
                                    size="small"
                                />
                            }
                            label="Force run daily summary"
                        />
                        <Button variant="outlined" onClick={onRunPoller} disabled={pollerStatus === 'running'}>
                            Run poller
                        </Button>
                        {pollerStatus === 'running' && (
                            <Typography variant="body2" color="text.secondary">
                                Triggering…
                            </Typography>
                        )}
                        {pollerStatus === 'ok' && (
                            <Typography variant="body2" color="success.main">
                                Poller triggered
                            </Typography>
                        )}
                        {pollerStatus === 'error' && (
                            <Typography variant="body2" color="error">
                                Failed to trigger poller
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <h4>Inject shipment data</h4>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Manually creates shipment/cargo tracking rows for the given orders, as if they had been recorded
                        via an incoming email + excel attachment. For testing invoice shipment info display only.
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 900 }}>
                        <Select
                            displayEmpty
                            size="small"
                            value={shipmentProviderId}
                            onChange={(e) => setShipmentProviderId(e.target.value)}
                            sx={{ maxWidth: 300 }}>
                            <MenuItem value="" disabled>
                                Select cargo provider
                            </MenuItem>
                            {(cargoProviders || []).map((p) => (
                                <MenuItem key={p.clusterId} value={p.clusterId}>
                                    {p.name}
                                </MenuItem>
                            ))}
                        </Select>
                        {shipmentOrders.map((row, index) => (
                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <TextField
                                    size="small"
                                    label="Order number"
                                    value={row.orderNumber}
                                    onChange={(e) => onShipmentOrderChange(index, 'orderNumber', e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    label="Grouped with order #"
                                    value={row.groupedWithOrderNumber}
                                    onChange={(e) =>
                                        onShipmentOrderChange(index, 'groupedWithOrderNumber', e.target.value)
                                    }
                                />
                                <TextField
                                    size="small"
                                    label="Boxes"
                                    value={row.boxes}
                                    onChange={(e) => onShipmentOrderChange(index, 'boxes', e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    label="Parcels"
                                    value={row.parcels}
                                    onChange={(e) => onShipmentOrderChange(index, 'parcels', e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    label="Pallets eur"
                                    value={row.palletsEur}
                                    onChange={(e) => onShipmentOrderChange(index, 'palletsEur', e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    label="Pallets teho"
                                    value={row.palletsTeho}
                                    onChange={(e) => onShipmentOrderChange(index, 'palletsTeho', e.target.value)}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => onRemoveShipmentOrderRow(index)}
                                    disabled={shipmentOrders.length === 1}>
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ))}
                        <Box>
                            <Button size="small" startIcon={<AddIcon />} onClick={onAddShipmentOrderRow}>
                                Add order
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Button
                                variant="outlined"
                                onClick={onInjectShipment}
                                disabled={
                                    shipmentStatus === 'running' ||
                                    !shipmentProviderId ||
                                    shipmentOrders.some((o) => !o.orderNumber)
                                }>
                                Inject shipment data
                            </Button>
                            {shipmentStatus === 'running' && (
                                <Typography variant="body2" color="text.secondary">
                                    Injecting…
                                </Typography>
                            )}
                            {shipmentStatus === 'ok' && (
                                <Typography variant="body2" color="success.main">
                                    Shipment data injected
                                </Typography>
                            )}
                            {shipmentStatus === 'error' && (
                                <Typography variant="body2" color="error">
                                    Failed to inject shipment data
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <h4>Change order status</h4>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Changes an order's status directly, using the same endpoint as the production view (requires
                        Production/Sales/Invoicing role). Reason is only used for the "DeliveredNonInvoicable" status.
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <TextField
                            size="small"
                            label="Order number"
                            value={statusOrderNumber}
                            onChange={(e) => setStatusOrderNumber(e.target.value)}
                        />
                        <Select
                            displayEmpty
                            size="small"
                            value={statusNewStatus}
                            onChange={(e) => setStatusNewStatus(e.target.value)}
                            sx={{ minWidth: 200 }}>
                            <MenuItem value="" disabled>
                                Select new status
                            </MenuItem>
                            {ORDER_STATUS_OPTIONS.map((s) => (
                                <MenuItem key={s.value} value={s.value}>
                                    {s.label}
                                </MenuItem>
                            ))}
                        </Select>
                        <TextField
                            size="small"
                            label="Reason (optional)"
                            value={statusReason}
                            onChange={(e) => setStatusReason(e.target.value)}
                        />
                        <Button
                            variant="outlined"
                            onClick={onChangeOrderStatus}
                            disabled={
                                orderStatusUpdateStatus === 'running' || !statusOrderNumber || !statusNewStatus
                            }>
                            Change status
                        </Button>
                        {orderStatusUpdateStatus === 'running' && (
                            <Typography variant="body2" color="text.secondary">
                                Updating…
                            </Typography>
                        )}
                        {orderStatusUpdateStatus === 'ok' && (
                            <Typography variant="body2" color="success.main">
                                Status updated
                            </Typography>
                        )}
                        {orderStatusUpdateStatus === 'error' && (
                            <Typography variant="body2" color="error">
                                Failed to update status
                            </Typography>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12}>
                    <h4>ws events</h4>
                    <table>
                        <thead>
                            <tr>
                                <td>ts</td>
                                <td>event</td>
                            </tr>
                        </thead>
                        <tbody>
                            {orderBy(wsEvents, ['timestamp'], ['desc']).map((e) => (
                                <tr key={e.timestamp}>
                                    <td>{e.timestamp}</td>
                                    <td>{e.event}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Grid>
                <Grid item xs={12}>
                    <h4>Lifecycle events</h4>
                    <table>
                        <thead>
                            <tr>
                                <td>ts</td>
                                <td>event</td>
                            </tr>
                        </thead>
                        <tbody>
                            {orderBy(lifecycleEvents, ['timestamp'], ['desc']).map((e) => (
                                <tr key={e.timestamp}>
                                    <td>{e.timestamp}</td>
                                    <td>
                                        {e.old} ... {e.new}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Grid>
            </Grid>
        </div>
    );
}
